"use client";

import React, { useState, useEffect, useCallback } from "react";
import { cn } from "@repo/utils/cn";
import { tv, VariantProps } from "tailwind-variants";
import { sectionWrapper } from "../sectionWrappers";
import { SectionTitle, SectionTitleProps } from "../SectionTitle";
import { Slider } from "../Slider";
import { CalculatorResults, CalculatorResult } from "../CalculatorResults";
import { calculateResultValues } from "../utils/calculator";
import type { ButtonProps } from "../Button";

const calculatorSection = tv({
  base: "uipm:w-full uipm:px-[var(--container-margin)]",
});

const calculatorLayout = tv({
  base: cn(
    "uipm:flex uipm:flex-col uipm:lg:flex-row uipm:gap-[var(--container-gutter)] uipm:items-start uipm:justify-start",
    "uipm:w-full",
  ),
});

const leftColumn = tv({
  base: cn(
    "uipm:flex uipm:flex-col uipm:gap-8 uipm:flex-1 uipm:min-w-[825px]",
    "uipm:lg:basis-0 uipm:lg:grow uipm:lg:shrink-0",
  ),
});

const rightColumn = tv({
  base: cn(
    "uipm:flex uipm:flex-col uipm:gap-[var(--container-gutter)] ",
    "uipm:w-full uipm:lg:w-[593px]",
  ),
});

const textSection = tv({
  base: "uipm:flex uipm:flex-col uipm:gap-[var(--container-gutter)] uipm:pr-0 uipm:lg:pr-[50px]",
});

const slidersSection = tv({
  base: "uipm:flex uipm:flex-col uipm:gap-8",
});

export interface CalculatorSlider {
  _uid?: string;
  _editable?: string;
  title: string;
  min: number;
  max: number;
  defaultValue: number;
  [key: string]: any;
}

export interface CalculatorResultItem {
  _uid?: string;
  _editable?: string;
  title: string;
  subtitle: string;
  description?: string;
  [key: string]: any;
}

export interface CalculatorSectionProps
  extends VariantProps<typeof sectionWrapper> {
  id?: string;
  head?: SectionTitleProps;
  description?: string;
  sliders?: CalculatorSlider[];
  totalLabel?: string;
  totalUnits?: string;
  resultDescription?: string;
  button?: ButtonProps;
  resultItems?: CalculatorResultItem[];
  variant?: "simple" | "detailed";
  className?: string;
  [key: string]: any;
}

export const CalculatorSection = React.forwardRef<
  HTMLElement,
  CalculatorSectionProps
>(
  (
    {
      head,
      description,
      sliders = [],
      totalLabel,
      totalUnits,
      resultDescription,
      button,
      resultItems = [],
      variant = "simple",
      backgroundColor = "none",
      topPadding = "none",
      bottomPadding = "none",
      className,
      id,
      ...props
    },
    ref,
  ) => {
    const employeesSlider = sliders?.[0];
    const parkingsSlider = sliders?.[1];

    const initialEmployeeCount = employeesSlider?.defaultValue ?? 0;
    const initialParkingCount = parkingsSlider?.defaultValue ?? 0;

    const [employeeCount, setEmployeeCount] = useState(initialEmployeeCount);
    const [parkingCount, setParkingCount] = useState(initialParkingCount);
    const [calculatedResults, setCalculatedResults] = useState(
      calculateResultValues(initialParkingCount, initialEmployeeCount),
    );

    useEffect(() => {
      const newResults = calculateResultValues(parkingCount, employeeCount);
      setCalculatedResults(newResults);
    }, [employeeCount, parkingCount]);

    const handleEmployeeChange = useCallback((value: number) => {
      setEmployeeCount(value);
    }, []);

    const handleParkingChange = useCallback((value: number) => {
      setParkingCount(value);
    }, []);

    if (!employeesSlider || !parkingsSlider || !sliders || sliders.length < 2) {
      return null;
    }

    const calculatorResultsData: CalculatorResult[] = (() => {
      if (resultItems && resultItems.length > 0) {
        const itemsToShow = variant === "detailed" ? resultItems.slice(0, 3) : resultItems.slice(0, 2);

        return itemsToShow.map((item: CalculatorResultItem, index: number) => {
          let value = calculatedResults.parking;

          if (variant === "detailed") {
            if (index === 0) value = calculatedResults.parking;
            else if (index === 1) value = calculatedResults.reporting;
            else if (index === 2) value = calculatedResults.administration;
          } else {
            if (index === 0) value = calculatedResults.parking;
            else value = calculatedResults.administration;
          }

          return {
            label: item.subtitle,
            value,
            tooltip: item.description,
            _uid: item._uid,
            _editable: item._editable,
          };
        });
      }

      return [];
    })();

    return (
      <section
        id={id}
        ref={ref}
        className={sectionWrapper({
          backgroundColor,
          topPadding,
          bottomPadding,
        })}
        {...props}
      >
        <div className={calculatorSection()}>
          <div className={cn(calculatorLayout(), className)}>
            <div className={leftColumn()}>
              <div className={textSection()}>
                {head && <SectionTitle {...head} />}
                {description && (
                  <div className="uipm:font-['Inter'] uipm:font-normal uipm:text-[16px] uipm:leading-[26px] uipm:text-[#575757]">
                    {description}
                  </div>
                )}
              </div>

              <div className={slidersSection()}>
                {employeesSlider && (
                  <Slider
                    label={employeesSlider.title}
                    min={employeesSlider.min}
                    max={employeesSlider.max}
                    defaultValue={employeesSlider.defaultValue}
                    value={employeeCount}
                    onChange={handleEmployeeChange}
                    showValue
                  />
                )}
                {parkingsSlider && (
                  <Slider
                    label={parkingsSlider.title}
                    min={parkingsSlider.min}
                    max={parkingsSlider.max}
                    defaultValue={parkingsSlider.defaultValue}
                    value={parkingCount}
                    onChange={handleParkingChange}
                    showValue
                  />
                )}
              </div>
            </div>

            <div className={rightColumn()}>
              <CalculatorResults
                totalResult={calculatedResults.total}
                totalLabel={totalLabel || ""}
                totalUnits={totalUnits || ""}
                results={calculatorResultsData}
                description={resultDescription}
                button={button}
              />
            </div>
          </div>
        </div>
      </section>
    );
  },
);

CalculatorSection.displayName = "CalculatorSection";
